/* eslint-disable */
import React from 'react'
import PropTypes from 'prop-types'
import { Badge, Divider, Form, Popconfirm, Select, Space, Spin, Tag, Tooltip, Typography } from 'antd'
import { CloseOutlined, UserAddOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import { getSystemAgentsList } from '@/service/systemControl'
import { renderTextStr } from '@/utils/renderUtils'

const { Text } = Typography

/** 与「分配角色」弹窗一致 */
const DEFAULT_AGENT_QUERY = {
  pageNumber: 1,
  pageSize: 50,
  orderBy: 'create_time',
  orderDirection: 'desc',
  state: 1
}

const SECTION_BOX = {
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  background: 'rgba(0,0,0,0.02)'
}

function agentOptionLabel(a) {
  const acc = renderTextStr(a.account)
  const name = renderTextStr(a.userName)
  const store = renderTextStr(a.storeName)
  const mid = `${acc} · ${name}`
  return store ? `${mid} · ${store}` : mid
}

function roleUserRowKey(u) {
  const idPart = u.id != null ? String(u.id) : `noid`
  const storePart = u.storeId != null && u.storeId !== '' ? String(u.storeId) : 'nostore'
  return `${idPart}-${storePart}`
}

function storeDisplayName(storeId, storeNameById) {
  if (!storeNameById || typeof storeNameById.get !== 'function') {
    if (storeId == null || storeId === '') return '未指定门店'
    return `门店#${String(storeId)}`
  }
  if (storeId == null || storeId === '') {
    return '未指定门店'
  }
  const k = String(storeId)
  const name = storeNameById.get(k)
  if (name) return name
  return `门店#${k}`
}

/**
 * 成员管理：已有成员以 Tag 紧凑展示；移除带 Popconfirm。
 */
class RoleAddMemberForm extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    roleRecord: PropTypes.object,
    storeNameById: PropTypes.object,
    onRemoveMember: PropTypes.func,
    onAddMember: PropTypes.func
  }

  state = {
    agentOptions: [],
    agentsLoading: false,
    localUsers: [],
    removingAgentId: null,
    addingAgentId: null
  }

  constructor(props) {
    super(props)
    this.agentSearchSeq = 0
    this.debouncedLoadAgents = debounce((keyword) => {
      this.loadAgents(keyword)
    }, 300)
  }

  componentDidMount() {
    this.syncUsersFromProps()
    this.loadAgents('')
  }

  componentDidUpdate(prevProps) {
    if (prevProps.roleRecord !== this.props.roleRecord) {
      this.syncUsersFromProps()
    }
  }

  componentWillUnmount() {
    this.debouncedLoadAgents.cancel()
  }

  syncUsersFromProps = () => {
    const u = this.props.roleRecord?.users
    this.setState({ localUsers: Array.isArray(u) ? [...u] : [] })
  }

  loadAgents = async (keywordRaw) => {
    const kw = typeof keywordRaw === 'string' ? keywordRaw.trim() : ''
    const seq = ++this.agentSearchSeq
    this.setState({ agentsLoading: true })
    try {
      const res = await getSystemAgentsList({
        ...DEFAULT_AGENT_QUERY,
        ...(kw !== '' ? { keyword: kw } : {})
      })
      if (seq !== this.agentSearchSeq) {
        return
      }
      const body = res?.data ?? {}
      const list = Array.isArray(body.list)
        ? body.list
        : Array.isArray(body.records)
          ? body.records
          : []
      const agentOptions = list
        .filter((a) => a && a.id != null)
        .map((a) => ({
          value: String(a.id),
          label: agentOptionLabel(a)
        }))
      this.setState({ agentOptions })
    } catch (e) {
      if (seq === this.agentSearchSeq) {
        this.setState({ agentOptions: [] })
      }
    } finally {
      if (seq === this.agentSearchSeq) {
        this.setState({ agentsLoading: false })
      }
    }
  }

  handleAgentSearch = (value) => {
    this.setState({ agentsLoading: true })
    this.agentSearchSeq += 1
    this.debouncedLoadAgents(value || '')
  }

  /** 下拉选中即加入，无需再点弹窗确定 */
  handlePickAgent = async (val) => {
    const { form, onAddMember } = this.props
    if (val == null || val === '') {
      return
    }
    if (typeof onAddMember !== 'function') {
      return
    }
    const idStr = String(val)
    this.setState({ addingAgentId: idStr })
    try {
      await onAddMember(val)
      form?.current?.setFieldsValue({ agentId: undefined })
    } catch (e) {
      form?.current?.setFieldsValue({ agentId: undefined })
    } finally {
      this.setState({ addingAgentId: null })
    }
  }

  handleRemoveClick = async (agentId) => {
    const { onRemoveMember } = this.props
    if (typeof onRemoveMember !== 'function') return
    this.setState({ removingAgentId: String(agentId) })
    try {
      await onRemoveMember(agentId)
      this.setState((prev) => ({
        localUsers: prev.localUsers.filter((u) => String(u.id) !== String(agentId)),
        removingAgentId: null
      }))
    } catch (e) {
      this.setState({ removingAgentId: null })
    }
  }

  render() {
    const { form, roleRecord, storeNameById, onRemoveMember, onAddMember } = this.props
    const { agentOptions, agentsLoading, localUsers, removingAgentId, addingAgentId } = this.state
    const roleName = renderTextStr(roleRecord?.name)
    const memberCount = Array.isArray(localUsers) ? localUsers.length : 0
    const canRemove = typeof onRemoveMember === 'function'
    const canAdd = typeof onAddMember === 'function'
    const existingIds = new Set((localUsers || []).map((u) => String(u.id)))
    const selectOptions = agentOptions.filter((o) => !existingIds.has(String(o.value)))
    /** 搜索加载中不禁用输入框，否则无法继续输入关键字 */
    const selectDisabled = !canAdd || addingAgentId != null
    const selectLoading = agentsLoading || addingAgentId != null

    return (
      <Form ref={form} layout="vertical" style={{ width: '100%' }} initialValues={{ agentId: undefined }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 14, fontSize: 12, lineHeight: 1.5 }}>
          角色「{roleName || '—'}」：下方可一并查看成员、移除或搜索添加；<Text strong>选中人员即加入</Text>，点「完成」仅关闭窗口。
        </Text>

        <div style={{ ...SECTION_BOX, padding: '10px 12px 12px', marginBottom: 12 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
              gap: 8
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Text strong style={{ fontSize: 13 }}>已有成员</Text>
              <Badge count={memberCount} overflowCount={999} color="#1677ff" />
            </span>
          </div>
          {!canRemove ? (
            <Text type="secondary">未配置移除</Text>
          ) : memberCount === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              暂无成员，可在下方添加
            </Text>
          ) : (
            <div style={{ maxHeight: 140, overflowY: 'auto', paddingRight: 2 }}>
              <Space size={[6, 6]} wrap>
                {localUsers.map((u) => {
                  const key = roleUserRowKey(u)
                  const name = renderTextStr(u.userName)
                  const store = storeDisplayName(u.storeId, storeNameById)
                  const fullLabel = `${name} · ${store}`
                  const busy = removingAgentId != null && String(removingAgentId) === String(u.id)
                  return (
                    <Tag
                      key={key}
                      style={{
                        marginInlineEnd: 0,
                        padding: '2px 6px',
                        fontSize: 12,
                        lineHeight: 1.45,
                        maxWidth: '100%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <Tooltip title={fullLabel}>
                        <Text ellipsis style={{ marginBottom: 0, maxWidth: 220, fontSize: 12 }}>
                          {fullLabel}
                        </Text>
                      </Tooltip>
                      <Popconfirm
                        title="从本角色移除此成员？"
                        okText="移除"
                        cancelText="取消"
                        okType="danger"
                        okButtonProps={{ loading: busy }}
                        onConfirm={() => this.handleRemoveClick(u.id)}
                      >
                        <CloseOutlined
                          style={{
                            cursor: 'pointer',
                            color: 'rgba(0, 0, 0, 0.45)',
                            flexShrink: 0,
                            fontSize: 10
                          }}
                        />
                      </Popconfirm>
                    </Tag>
                  )
                })}
              </Space>
            </div>
          )}
        </div>

        <Divider plain style={{ margin: '12px 0' }} />

        <div style={{ ...SECTION_BOX, padding: '12px 12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <UserAddOutlined style={{ color: 'var(--ant-color-primary, #1677ff)', fontSize: 16 }} />
            <Text strong style={{ fontSize: 13 }}>
              添加成员
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              搜索后点选一条，立即加入
            </Text>
          </div>
          <Form.Item name="agentId" noStyle>
            <Select
              showSearch
              allowClear
              disabled={selectDisabled}
              loading={selectLoading}
              placeholder={canAdd ? '输入关键字搜索，选中即加入本角色' : '未配置添加'}
              filterOption={false}
              onSearch={this.handleAgentSearch}
              onChange={this.handlePickAgent}
              options={selectOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
              popupMatchSelectWidth
              notFoundContent={
                agentsLoading ? (
                  <div style={{ padding: '12px 0', textAlign: 'center' }}>
                    <Spin size="small" />
                  </div>
                ) : (
                  <Text type="secondary" style={{ display: 'block', padding: '8px 12px', fontSize: 12 }}>
                    无匹配人员；改关键词或稍后再试
                  </Text>
                )
              }
            />
          </Form.Item>
        </div>
      </Form>
    )
  }
}

export default RoleAddMemberForm
